import {
  buildUnifiedCrmContact,
  fetchCrmJson,
  type CrmContactAssociation,
  type CrmContactTenant,
  type CrmFetchOptions,
  type UnifiedCrmContact,
} from "@/src/features/integrations/vendors/crm/contactAdapter";
import { requireProviderArray } from "@/src/features/integrations/providerHttpFailure";
import { normalizeSalesforceInstanceUrl } from "./urls";

export { normalizeSalesforceInstanceUrl } from "./urls";

export const DEFAULT_SALESFORCE_API_VERSION = "v61.0";

const SALESFORCE_CONTACT_FIELDS = [
  "Id",
  "FirstName",
  "MiddleName",
  "LastName",
  "Salutation",
  "Suffix",
  "Name",
  "Email",
  "OtherEmail",
  "Phone",
  "MobilePhone",
  "HomePhone",
  "OtherPhone",
  "Title",
  "Department",
  "MailingStreet",
  "MailingCity",
  "MailingState",
  "MailingPostalCode",
  "MailingCountry",
  "Birthdate",
  "Description",
  "CreatedDate",
  "LastModifiedDate",
  "SystemModstamp",
  "IsDeleted",
] as const;

const SALESFORCE_CONTACT_ORGANIZATION_FIELDS = [
  "AccountId",
  "Account.Name",
] as const;

function salesforceContactFields(includeOrganizations = false): string[] {
  return [
    ...SALESFORCE_CONTACT_FIELDS,
    ...(includeOrganizations ? SALESFORCE_CONTACT_ORGANIZATION_FIELDS : []),
  ];
}

export type RawSalesforceContact = {
  Id: string;
  FirstName?: string | null;
  MiddleName?: string | null;
  LastName?: string | null;
  Salutation?: string | null;
  Suffix?: string | null;
  Name?: string | null;
  Email?: string | null;
  OtherEmail?: string | null;
  Phone?: string | null;
  MobilePhone?: string | null;
  HomePhone?: string | null;
  OtherPhone?: string | null;
  AccountId?: string | null;
  Account?: { Id?: string | null; Name?: string | null } | null;
  Title?: string | null;
  Department?: string | null;
  MailingStreet?: string | null;
  MailingCity?: string | null;
  MailingState?: string | null;
  MailingPostalCode?: string | null;
  MailingCountry?: string | null;
  Birthdate?: string | null;
  Description?: string | null;
  CreatedDate?: string;
  LastModifiedDate?: string;
  SystemModstamp?: string;
  IsDeleted?: boolean;
};

type SalesforceContactPagePayload = {
  records?: RawSalesforceContact[];
  done?: boolean;
  totalSize?: number;
  nextRecordsUrl?: string;
};

export type SalesforceContactCursor = {
  nextRecordsUrl: string;
};

export type SalesforceContactPage = {
  contacts: RawSalesforceContact[];
  nextCursor: SalesforceContactCursor | null;
};

function salesforceApiVersion(value?: string): string {
  const version = String(value ?? DEFAULT_SALESFORCE_API_VERSION).trim();
  if (!/^v\d{2,3}\.\d$/.test(version)) {
    throw new Error("Salesforce REST API version is invalid");
  }
  return version;
}

/** OAuth `instance_url` is account data and must never become an arbitrary SSRF target. */
function salesforceCursorUrl(instanceUrl: string, cursor: string): URL {
  const baseUrl = normalizeSalesforceInstanceUrl(instanceUrl);
  const url = new URL(cursor, `${baseUrl}/`);
  if (
    url.origin !== baseUrl
    || !url.pathname.startsWith("/services/data/")
    || url.username
    || url.password
  ) {
    throw new Error("Salesforce contact sync rejected an invalid query cursor");
  }
  return url;
}

export function buildSalesforceContactSoql(
  incrementalAfter?: number,
  includeOrganizations = false,
): string {
  const since = typeof incrementalAfter === "number"
    && Number.isFinite(incrementalAfter)
    && incrementalAfter > 0
    ? ` WHERE SystemModstamp >= ${new Date(incrementalAfter).toISOString()}`
    : "";
  return [
    `SELECT ${salesforceContactFields(includeOrganizations).join(", ")}`,
    `FROM Contact${since}`,
    "ORDER BY SystemModstamp ASC, Id ASC",
  ].join(" ");
}

/** One resumable queryAll page, including Salesforce deletion tombstones. */
export async function fetchSalesforceContactPage(args: {
  accessToken: string;
  instanceUrl: string;
  cursor?: SalesforceContactCursor | null;
  incrementalAfter?: number;
  includeOrganizations?: boolean;
  apiVersion?: string;
  options?: CrmFetchOptions;
}): Promise<SalesforceContactPage> {
  const baseUrl = normalizeSalesforceInstanceUrl(args.instanceUrl);
  let url: URL;
  if (args.cursor?.nextRecordsUrl) {
    url = salesforceCursorUrl(baseUrl, args.cursor.nextRecordsUrl);
  } else {
    url = new URL(
      `/services/data/${salesforceApiVersion(args.apiVersion)}/queryAll`,
      `${baseUrl}/`,
    );
    url.searchParams.set(
      "q",
      buildSalesforceContactSoql(args.incrementalAfter, args.includeOrganizations),
    );
  }
  const page = await fetchCrmJson<SalesforceContactPagePayload>({
    provider: "salesforce",
    url,
    accessToken: args.accessToken,
    options: args.options,
  });
  const nextRecordsUrl = String(page.nextRecordsUrl ?? "").trim();
  if (nextRecordsUrl) salesforceCursorUrl(baseUrl, nextRecordsUrl);
  return {
    contacts: requireProviderArray(page, "salesforce", "records") as RawSalesforceContact[],
    nextCursor: page.done === true || !nextRecordsUrl
      ? null
      : { nextRecordsUrl },
  };
}

function salesforceRevisionAt(contact: RawSalesforceContact): number {
  const parsed = Date.parse(contact.SystemModstamp ?? contact.LastModifiedDate ?? "");
  return Number.isNaN(parsed) ? 0 : parsed;
}

function dedupeRawSalesforceContacts(
  contacts: RawSalesforceContact[],
): RawSalesforceContact[] {
  const byId = new Map<string, RawSalesforceContact>();
  for (const contact of contacts) {
    const id = String(contact.Id ?? "").trim();
    if (!id) continue;
    const existing = byId.get(id);
    if (
      !existing
      || salesforceRevisionAt(contact) > salesforceRevisionAt(existing)
      || (
        salesforceRevisionAt(contact) === salesforceRevisionAt(existing)
        && contact.IsDeleted === true
      )
    ) {
      byId.set(id, contact);
    }
  }
  return Array.from(byId.values());
}

export async function fetchAllSalesforceContacts(args: {
  accessToken: string;
  instanceUrl: string;
  cursor?: SalesforceContactCursor | null;
  incrementalAfter?: number;
  includeOrganizations?: boolean;
  apiVersion?: string;
  options?: CrmFetchOptions;
  maxPages?: number;
}): Promise<RawSalesforceContact[]> {
  const contacts: RawSalesforceContact[] = [];
  let cursor = args.cursor ?? null;
  let firstPage = !cursor;
  const seenCursors = new Set<string>();
  const maxPages = Math.max(1, Math.min(args.maxPages ?? 100_000, 100_000));

  for (let pageCount = 0; pageCount < maxPages; pageCount += 1) {
    const page = await fetchSalesforceContactPage({
      accessToken: args.accessToken,
      instanceUrl: args.instanceUrl,
      cursor,
      incrementalAfter: firstPage ? args.incrementalAfter : undefined,
      includeOrganizations: args.includeOrganizations,
      apiVersion: args.apiVersion,
      options: args.options,
    });
    firstPage = false;
    contacts.push(...page.contacts);
    cursor = page.nextCursor;
    if (!cursor) return dedupeRawSalesforceContacts(contacts);
    if (seenCursors.has(cursor.nextRecordsUrl)) {
      throw new Error("Salesforce contact pagination repeated a query cursor");
    }
    seenCursors.add(cursor.nextRecordsUrl);
  }

  throw new Error("Salesforce contact pagination exceeded the page limit");
}

function salesforceAssociation(contact: RawSalesforceContact): CrmContactAssociation[] {
  const providerObjectId = String(contact.AccountId ?? contact.Account?.Id ?? "").trim();
  return providerObjectId
    ? [{
        providerObjectType: "account",
        providerObjectId,
        name: String(contact.Account?.Name ?? "").trim() || undefined,
        relationship: "Contact.AccountId",
      }]
    : [];
}

function optionalTimestamp(value: unknown): number | undefined {
  const parsed = Date.parse(String(value ?? ""));
  return Number.isNaN(parsed) ? undefined : parsed;
}

export function normalizeSalesforceContact(args: {
  contact: RawSalesforceContact;
  tenant: CrmContactTenant;
  includeOrganizations?: boolean;
}): UnifiedCrmContact | null {
  const contact = args.contact;
  const displayName = String(
    contact.Name
      ?? [contact.FirstName, contact.MiddleName, contact.LastName]
        .filter(Boolean)
        .join(" "),
  ).trim();
  const phoneEntries = [
    { value: contact.MobilePhone, type: "mobile" },
    { value: contact.Phone, type: "work" },
    { value: contact.HomePhone, type: "home" },
    { value: contact.OtherPhone, type: "other" },
  ].filter((entry) => String(entry.value ?? "").trim());
  const emailEntries = [contact.Email, contact.OtherEmail]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);
  const accountName = args.includeOrganizations
    ? String(contact.Account?.Name ?? "").trim()
    : "";
  const hasAddress = [
    contact.MailingStreet,
    contact.MailingCity,
    contact.MailingState,
    contact.MailingPostalCode,
    contact.MailingCountry,
  ].some(Boolean);
  const birthdayDate = contact.Birthdate
    ? new Date(`${contact.Birthdate}T00:00:00.000Z`)
    : null;
  const revisionAt = salesforceRevisionAt(contact);
  const sourceRevision = [
    "salesforce",
    String(contact.Id ?? "").trim(),
    String(revisionAt),
    contact.IsDeleted ? "deleted" : "active",
  ].join(":");

  return buildUnifiedCrmContact({
    provider: "salesforce",
    providerContactId: contact.Id,
    tenant: args.tenant,
    names: {
      displayName: displayName.toLowerCase(),
      givenName: String(contact.FirstName ?? "").trim().toLowerCase(),
      familyName: String(contact.LastName ?? "").trim().toLowerCase(),
      middleName: String(contact.MiddleName ?? "").trim().toLowerCase(),
      honorificPrefix: String(contact.Salutation ?? "").trim() || undefined,
      honorificSuffix: String(contact.Suffix ?? "").trim() || undefined,
    },
    emailAddresses: emailEntries.map((value, index) => ({
      value,
      type: index === 0 ? "work" : "other",
      isPrimary: index === 0,
    })),
    phoneNumbers: phoneEntries.map((entry, index) => ({
      value: String(entry.value),
      type: entry.type,
      isPrimary: index === 0,
    })),
    organizations: accountName
      ? [{
          name: accountName,
          title: String(contact.Title ?? "").trim() || undefined,
          department: String(contact.Department ?? "").trim() || undefined,
        }]
      : [],
    addresses: hasAddress
      ? [{
          street: String(contact.MailingStreet ?? "").trim() || undefined,
          city: String(contact.MailingCity ?? "").trim() || undefined,
          state: String(contact.MailingState ?? "").trim() || undefined,
          postalCode: String(contact.MailingPostalCode ?? "").trim() || undefined,
          country: String(contact.MailingCountry ?? "").trim() || undefined,
          type: "work",
        }]
      : [],
    birthday: birthdayDate && !Number.isNaN(birthdayDate.getTime())
      ? {
          year: birthdayDate.getUTCFullYear(),
          month: birthdayDate.getUTCMonth() + 1,
          day: birthdayDate.getUTCDate(),
        }
      : undefined,
    notes: String(contact.Description ?? "").trim() || undefined,
    isDeleted: contact.IsDeleted === true,
    createdDate: optionalTimestamp(contact.CreatedDate),
    updatedDate: revisionAt || optionalTimestamp(contact.LastModifiedDate),
    sourceRevision,
    sourceRevisionAt: revisionAt,
    providerAssociations: args.includeOrganizations ? salesforceAssociation(contact) : [],
  });
}

function escapeSalesforceSoslTerm(value: string): string {
  const reserved = new Set(["?", "&", "|", "!", "{", "}", "[", "]", "(", ")", "^", "~", "*", ":", "\\", "\"", "'", "+", "-"]);
  return Array.from(value).map((character) =>
    reserved.has(character) ? `\\${character}` : character
  ).join("");
}

export async function searchSalesforceContacts(args: {
  accessToken: string;
  instanceUrl: string;
  query: string;
  includeOrganizations?: boolean;
  apiVersion?: string;
  options?: CrmFetchOptions;
}): Promise<RawSalesforceContact[]> {
  const query = args.query.trim();
  if (!query) return [];
  const baseUrl = normalizeSalesforceInstanceUrl(args.instanceUrl);
  const url = new URL(
    `/services/data/${salesforceApiVersion(args.apiVersion)}/search`,
    `${baseUrl}/`,
  );
  const sosl = `FIND {${escapeSalesforceSoslTerm(query)}} IN ALL FIELDS RETURNING Contact(${salesforceContactFields(args.includeOrganizations).join(", ")})`;
  url.searchParams.set("q", sosl);
  const result = await fetchCrmJson<{ searchRecords?: RawSalesforceContact[] }>({
    provider: "salesforce",
    url,
    accessToken: args.accessToken,
    options: args.options,
  });
  return dedupeRawSalesforceContacts(
    requireProviderArray(result, "salesforce", "searchRecords") as RawSalesforceContact[],
  );
}

// Owner rule (2026-09-23): Smart Manager never writes to Salesforce. Contacts
// are read (SOQL/SOSL, change events); edits stay inside NV Zero.

export type SalesforceContactChangeEvent = {
  organizationId: string;
  replayId: string;
  ChangeEventHeader: {
    entityName: string;
    recordIds: string[];
    changeType: "CREATE" | "UPDATE" | "DELETE" | "UNDELETE" | "GAP_OVERFLOW" | string;
    commitTimestamp: number;
    commitNumber?: number;
    sequenceNumber?: number;
    transactionKey?: string;
  };
};

/** Pub/Sub is OAuth-authenticated; this validates the bound org and event owner. */
export function parseSalesforceContactChangeEvent(args: {
  event: unknown;
  expectedOrganizationId: string;
}): SalesforceContactChangeEvent {
  if (!args.event || typeof args.event !== "object") {
    throw new Error("Salesforce contact change event is invalid");
  }
  const event = args.event as SalesforceContactChangeEvent;
  if (
    event.organizationId !== args.expectedOrganizationId
    || event.ChangeEventHeader?.entityName !== "Contact"
    || !Array.isArray(event.ChangeEventHeader?.recordIds)
    || event.ChangeEventHeader.recordIds.length === 0
    || event.ChangeEventHeader.recordIds.some((recordId) => !String(recordId ?? "").trim())
    || typeof event.ChangeEventHeader?.commitTimestamp !== "number"
    || !String(event.replayId ?? "").trim()
  ) {
    throw new Error("Salesforce contact change event ownership mismatch");
  }
  return event;
}

export function salesforceContactChangeDedupKey(
  event: SalesforceContactChangeEvent,
): string {
  const header = event.ChangeEventHeader;
  return [
    "salesforce",
    event.organizationId,
    event.replayId,
    header.transactionKey ?? "",
    header.sequenceNumber ?? "",
  ].join(":");
}
